import { Dropdown, type MenuProps } from 'antd';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import type File from '../../interfaces/File';
import type FileAction from '../../interfaces/FileAction';
import { type FileActionKey } from '../../lib/fileActions';
import { getFileTitle, getFolderHex, removeShortcutExtension } from '../../utils/utils';
import FileIcon from '../Common/FileIcon';
import TruncatedTooltip from '../Common/TruncatedTooltip';
import { Icon } from '../Core/Icon';

interface FileContextMenuProps {
  files: File[];
  position: { x: number; y: number; direction: 'up' | 'down' };
  visible: boolean;
  isUploadDisabledInCurrentFolder: boolean;
  onClose: () => void;
  onAction: (filesIds: number[], action: FileActionKey) => void;
  filesActions: FileAction[];
}

export const FileContextMenu: React.FC<FileContextMenuProps> = ({
  files,
  position,
  visible,
  onClose,
  onAction,
  filesActions,
}) => {
  const { t } = useTranslation();

  /**
   * @description Get the first file from the list of files.
   */
  const firstFile = useMemo(() => files?.[0], [files]);

  /**
   * An array of menu items generated from user actions. Each menu item defines
   * the properties necessary for rendering an individual menu option, including
   * label, icon, key, and optional danger or submenu attributes.
   *
   * This variable is memoized for performance optimization and dynamically
   * recomputed whenever `filteredActions` or `t` changes.
   *
   />
   <Tag
   key={tag.id}
   color={tag.color}
   onClick={() => {
   onAction(file!, `tag-${tag.id}`);
   }}
   >
   {tag.title}
   </Tag>
   </div>
   ),
   icon: null,
   })),
   } as FileAction;
   }
   return action;
   });
   }, [filteredFileActions, actionsContext, tags]);

   /**
   * An array of menu items generated from user actions. Each menu item defines
   * the properties necessary for rendering an individual menu option, including
   * label, icon, key, and optional danger or submenu attributes.
   *
   * This variable is memoized for performance optimization and dynamically
   * recomputed whenever `filteredActions` or `t` changes.
   *
   * Dependencies:
   * - `filteredActions`: A list of user actions filtered according to specific criteria.
   * - `t`: A translation function used to localize menu labels.
   *
   * Each menu item is created from `UserAction` objects through the use of the
   * `mapActionToMenuItem` function, which maps action properties to their
   * corresponding menu item fields.
   */
  const menuItems: MenuProps['items'] = useMemo(() => {
    const mapActionToMenuItem = (action: FileAction): NonNullable<MenuProps['items']>[number] => {
      if (action.type === 'divider') {
        return { type: 'divider' };
      }

      const baseItem = {
        label: action.label,
        disabled: action.disabled,
        icon: action.icon,
        key: action.key,
        danger: action.danger,
      };

      if (action.children) {
        return {
          ...baseItem,
          type: 'submenu',
          children: action.children.map(mapActionToMenuItem),
        };
      }
      return {
        ...baseItem,
        type: 'item',
      };
    };
    return filesActions.map(mapActionToMenuItem);
  }, [filesActions, t, files]);

  /**
   * Handles the click event for menu items.
   *
   * @type {MenuProps['onClick']}
   * @param {object} info - Information about the clicked menu item.
   * @param {string} info.key - The key of the clicked menu item.
   *
   * This function checks for the presence of a `user` object. If the user exists, it invokes the provided `onAction` callback
   * with the `user` object and the `key` of the clicked menu item. Finally, it calls the `onClose` function to perform the
   * necessary cleanup or closure tasks.
   */
  const handleMenuClick: MenuProps['onClick'] = (info) => {
    if (!files) return;
    //onClose();
    onAction(
      files.map((file) => file.id),
      info.key as FileMenuActionEnum,
    );
  };

  const menuWidth = 180;
  let left = position.x;
  let top = position.y;

  if (left + menuWidth > window.innerWidth) {
    left = window.innerWidth - menuWidth - 10;
  }

  return (
    <Dropdown
      autoAdjustOverflow
      getPopupContainer={() => document.body}
      menu={{
        items: [
          {
            label: (
              <span className="block w-[170px] truncate pl-2 text-sm text-gray-dark">
                {files.length > 1 ? (
                  <TruncatedTooltip>
                    {`${t('common.selectedItems')}: ${files.length}`}
                  </TruncatedTooltip>
                ) : (
                  <TruncatedTooltip>
                    {firstFile && removeShortcutExtension(getFileTitle(firstFile))}
                  </TruncatedTooltip>
                )}
              </span>
            ),
            disabled: true,
            key: 'file',
            icon: (
              <>
                {files.length > 1 ? (
                  <Icon name="FilesIcon" className="h-4 w-4" />
                ) : (
                  <FileIcon
                    isShortcut={firstFile?.isShortcut}
                    isFolder={firstFile?.isFolder}
                    fileExtension={removeShortcutExtension(firstFile?.fileExtension || '')}
                    className="h-4 w-4"
                    style={{
                      color: getFolderHex(firstFile!),
                    }}
                  />
                )}
              </>
            ),
          },
          {
            type: 'divider',
          },
          ...menuItems,
        ],
        onClick: handleMenuClick,
      }}
      overlayClassName="file-context-menu"
      trigger={['contextMenu']}
      open={visible}
      overlayStyle={{
        position: 'fixed',
        top,
        left,
        pointerEvents: 'auto',
        maxHeight: 'max-content',
        overflow: 'visible',
      }}
      transitionName={position.direction === 'up' ? 'slide-up-custom' : 'slide-down-custom'}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    />
  );
};

export default FileContextMenu;
